import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import type { IResponseIntegration, IResponseUserIntegration } from 'entities/Integrations'
import { IntegrationsApi } from 'entities/Integrations/api/integrations'
import { type IntegrationKey } from 'entities/Integrations/api/types'
import { Integration } from 'entities/Integrations/model/Integration'
import { CalendlyIntegrationStateStore } from 'entities/Integrations/store/CalendlyIntegrationStateStore'

export class IntegrationsStore {
  initPromise
  private setFinishInit: ((value?: unknown) => void) | null = null
  private _integrationsMap: Map<string, Integration> = new Map()
  private _loading = false
  private _isFullyFetched = false
  private _requestGetIntegrations: null | ReturnType<(typeof IntegrationsApi)['getIntegrations']> =
    null
  private _calendlyIntegrationStateStore = new CalendlyIntegrationStateStore()

  constructor() {
    makeAutoObservable(this)
    this.initPromise = new Promise((resolve) => {
      this.setFinishInit = resolve
    })
  }

  get calendlyIntegrationStateStore() {
    return this._calendlyIntegrationStateStore
  }

  fetchIntegrations = async () => {
    if (this._isFullyFetched) return
    if (this._requestGetIntegrations) return this._requestGetIntegrations.then()

    try {
      runInAction(() => {
        this._loading = true
      })

      this._requestGetIntegrations = IntegrationsApi.getIntegrations()

      this.setFinishInit?.()
      const {
        data: { data: integrations },
      } = await this._requestGetIntegrations

      integrations.map(this.addOrUpdateIntegration)

      runInAction(() => {
        this._isFullyFetched = true
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
        this._requestGetIntegrations = null
      })
    }
  }

  addOrUpdateIntegration = (item: IResponseIntegration) => {
    const integration = this._integrationsMap.get(item.key)

    if (integration) {
      integration.syncOrigin(item)

      return integration
    } else {
      const integration = new Integration(item)
      this._integrationsMap.set(integration.key, integration)

      return integration
    }
  }

  syncUserIntegration = (item: IResponseUserIntegration) => {
    this._integrationsMap.get(item.integration.key)?.syncUserIntegration(item)
  }

  getIntegration = (key?: IntegrationKey | string | null) => {
    if (!key) return null

    return this._integrationsMap.get(key) || null
  }

  getAsyncIntegration = async (key?: IntegrationKey | string | null) => {
    if (!key) return null

    const existingIntegration = this._integrationsMap.get(key)
    if (existingIntegration) {
      return existingIntegration
    }
    try {
      const { data } = await IntegrationsApi.getIntegrationByKey(key as IntegrationKey)
      return this.addOrUpdateIntegration(data)
    } catch (e) {
      console.error(e)
    }
  }

  getIntegrationById = (id: number) => {
    return this.integrations.find((integration) => integration.id === id)
  }

  getIntegrations = (keys: Array<IntegrationKey>) => {
    return keys
      .map((key) => this._integrationsMap.get(key))
      .filter((item) => Boolean(item)) as Integration[]
  }

  get loading() {
    return this._loading
  }

  get hasIntegrations() {
    return Boolean(this.integrations.length)
  }

  get integrations() {
    return Array.from(this._integrationsMap.values())
  }

  get connectedIntegrations() {
    return Array.from(this._integrationsMap.values()).filter(({ isConnected }) => isConnected)
  }
}
